import { t, type TranslationKey } from "@connect/i18n";
import type { CardErrorReason, ResolvedCardError } from "./card-error.types";

// Maps a POST /v3/cards decline reason to inline copy. Only reasons the add-card
// path actually returns are mapped; acquirer declines (insufficient funds,
// expired, limits, AVS) are transaction-only and intentionally fall through to
// the generic fallback. Ordered most-specific first so "(customer deleted
// recently)" wins over "Already in use".
const RULES: { reason: CardErrorReason; match: string[]; copy: TranslationKey }[] = [
  {
    reason: "alreadyInUseRecentlyDeleted",
    match: ["Already in use (customer deleted recently)"],
    copy: "addCard.errors.alreadyInUseRecentlyDeleted",
  },
  {
    reason: "alreadyInUse",
    match: ["Already in use"],
    copy: "addCard.errors.alreadyInUse",
  },
  {
    reason: "brandNotSupportedForSell",
    match: ["Card brand not supported for sell"],
    copy: "addCard.errors.brandNotSupportedForSell",
  },
  {
    reason: "brandNotSupportedInRegion",
    match: ["Card brand not supported in region"],
    copy: "addCard.errors.brandNotSupportedInRegion",
  },
  {
    reason: "invalidBrand",
    match: ["Invalid brand"],
    copy: "addCard.errors.invalidBrand",
  },
  {
    reason: "basicInfoMissing",
    match: ["Basic customer information must be provided before saving a card"],
    copy: "addCard.errors.basicInfoMissing",
  },
  // Transport-level failures (not API declines) — kept distinct from the generic
  // fallback so the copy stays actionable.
  {
    reason: "tokenization",
    match: ["tokeniz"],
    copy: "addCard.errors.tokenization",
  },
  {
    reason: "network",
    // "connect" also covers "connection" and "could not connect".
    match: ["network", "fetch", "connect"],
    copy: "addCard.errors.network",
  },
];

function extractMessage(error: unknown): string | undefined {
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }
  return undefined;
}

/**
 * Classifies an add-card failure (a typed `/v3/cards` error body, a thrown
 * Error, or a raw string) into a `CardErrorReason` + inline copy. Unmatched
 * causes fall back to a generic message. The wire code stays `generic`
 * regardless — this only drives the in-frame copy.
 */
export function resolveCardError(error: unknown): ResolvedCardError {
  const message = extractMessage(error)?.toLowerCase();
  if (message) {
    for (const rule of RULES) {
      if (rule.match.some((needle) => message.includes(needle.toLowerCase()))) {
        return { reason: rule.reason, message: t(rule.copy) };
      }
    }
  }
  return { reason: "unknown", message: t("addCard.errors.unknown") };
}
